import { useState } from "react";
import {
  createTrip,
  setActiveTrip,
  useFishStore,
} from "../lib/storage";
import { useBilling, hasProAccess, FREE_TRIP_LIMIT } from "../lib/billing";
import { navigate } from "../lib/router";
import { toast } from "./Toaster";
import { CheckIcon, PinIcon, PlusIcon } from "../ui";
import { BeachPicker } from "./BeachPicker";

const tripWord = (n: number) => (n === 1 ? "trip" : "trips");

// Compact "what trip am I logging to" control. While a trip is active, every
// species you mark seen is added to that trip's collection. A secondary row
// below the trip selector surfaces the trip's beach list — tap it to add /
// review the named snorkel spots this trip visited (attached to the trip,
// not to individual species, so we don't ask which beach every log).
export function TripBar() {
  const trips = useFishStore((s) => s.trips);
  const activeId = useFishStore((s) => s.active_trip_id ?? null);
  const [open, setOpen] = useState(false);
  const [beachPickerOpen, setBeachPickerOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const billing = useBilling();
  // Trial counts as Pro access. Existing trips are never touched — this
  // just blocks creating more once the free cap is reached.
  const atFreeLimit =
    billing !== null &&
    !hasProAccess(billing) &&
    trips.length >= FREE_TRIP_LIMIT;

  const active = trips.find((t) => t.id === activeId) || null;
  const beachCount = active?.beaches?.length ?? 0;

  const pick = (id: string | null) => {
    setActiveTrip(id);
    setOpen(false);
  };

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (atFreeLimit) {
      toast(
        `Free covers ${FREE_TRIP_LIMIT} ${tripWord(
          FREE_TRIP_LIMIT,
        )} — upgrade for unlimited.`,
      );
      navigate("/billing");
      return;
    }
    createTrip(name); // becomes active
    setNewName("");
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-h-[44px] w-full items-center justify-between rounded-xl bg-white/80 px-4 py-2.5 text-sm ring-1 ring-inset ring-ink/10 hover:ring-ink/20"
          aria-expanded={open}
        >
          <span className="text-ink/60">
            Logging to{" "}
            <span className="font-semibold text-ink">
              {active ? active.name : "no trip"}
            </span>
          </span>
          <span className="text-ink/40" aria-hidden>
            ▾
          </span>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl bg-white shadow-card ring-1 ring-ink/10">
            <button
              type="button"
              onClick={() => pick(null)}
              className={`flex min-h-[44px] w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-ink/5 ${
                !activeId ? "font-semibold text-ink" : "text-ink/70"
              }`}
            >
              No trip {!activeId && <CheckIcon className="h-4 w-4 text-aqua-deep" />}
            </button>
            {trips.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => pick(t.id)}
                className={`flex min-h-[44px] w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-ink/5 ${
                  t.id === activeId
                    ? "font-semibold text-ink"
                    : "text-ink/70"
                }`}
              >
                <span className="truncate">{t.name}</span>
                {t.id === activeId && <CheckIcon className="h-4 w-4 text-aqua-deep" />}
              </button>
            ))}
            {atFreeLimit ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/billing");
                }}
                className="flex w-full items-center justify-between gap-2 border-t border-ink/5 p-3 text-left text-sm"
              >
                <span className="text-ink/55">
                  Free covers {FREE_TRIP_LIMIT} {tripWord(FREE_TRIP_LIMIT)}.
                </span>
                <span className="shrink-0 font-bold text-aqua-deep">
                  Upgrade →
                </span>
              </button>
            ) : (
              <form
                onSubmit={add}
                className="flex gap-2 border-t border-ink/5 p-2"
              >
                <input
                  className="field py-2"
                  placeholder="New trip name…"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <button
                  type="submit"
                  className="btn-primary shrink-0 px-4 py-2"
                  disabled={!newName.trim()}
                >
                  Add
                </button>
              </form>
            )}
          </div>
        )}
      </div>

      {active && (
        <button
          type="button"
          onClick={() => setBeachPickerOpen(true)}
          className="flex w-full items-center gap-2.5 rounded-xl px-4 py-2 text-left text-[12.5px] text-meta transition hover:bg-ink/[0.03]"
          aria-label={
            beachCount > 0
              ? `Manage beaches on ${active.name}, ${beachCount} added`
              : `Add a beach to ${active.name}`
          }
        >
          <PinIcon className="h-4 w-4 flex-none text-aqua-deep" />
          {beachCount > 0 ? (
            <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
              <span className="truncate text-ink">
                {(active.beaches ?? []).slice(0, 3).join(" · ")}
              </span>
              {beachCount > 3 && (
                <span className="text-meta">+ {beachCount - 3}</span>
              )}
            </span>
          ) : (
            <span className="flex-1">Add a beach</span>
          )}
          <PlusIcon className="h-3.5 w-3.5 flex-none text-aqua-deep" />
        </button>
      )}

      {beachPickerOpen && active && (
        <BeachPicker
          tripId={active.id}
          onClose={() => setBeachPickerOpen(false)}
        />
      )}
    </div>
  );
}
